/**
 * Componente de sección de estadísticas por producto con SectionBox
 * Product statistics section component with SectionBox
 */
import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useMemo } from 'react';
import { ProductStatisticsTable } from './ProductStatisticsTable';
import { InventoryProduct } from '../../models/InventoryApiResponse';
import { FiltersSection } from '../interaction';
import { ColumnDef } from '@tanstack/react-table';

interface ProductStatisticsSectionProps {
  data: InventoryProduct[];
  total: number;
  handleSearch: (value: string) => void;
  onSalesFilterChange?: (value: 'all' | 'sold_last_30_days' | 'not_sold_last_30_days') => void;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (newSelected: string[]) => void;
  searchTerm: string;
  salesFilter?: 'all' | 'sold_last_30_days' | 'not_sold_last_30_days';
  isFetching?: boolean;
}

export const ProductStatisticsSection = ({
  data,
  total,
  handleSearch,
  onSalesFilterChange,
  columns,
  selectedColumns,
  onColumnsChange,
  searchTerm,
  salesFilter = 'all',
  isFetching = false,
}: ProductStatisticsSectionProps) => {
  // Detectar si hay filtros activos
  // Detect if there are active filters
  const hasActiveFilters = useMemo(() => {
    return (searchTerm && searchTerm.trim().length > 0) || (salesFilter && salesFilter !== 'all');
  }, [searchTerm, salesFilter]);

  return (
    <>
      {/* Barra de controles - Controls bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        {/* Controles izquierda - Left controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <FiltersSection
            handleSearch={handleSearch}
            onSalesFilterChange={onSalesFilterChange}
            columns={columns}
            selectedColumns={selectedColumns}
            onColumnsChange={onColumnsChange}
            searchTerm={searchTerm}
            salesFilter={salesFilter}
          />
        </div>
        {/* Total de resultados - Total results */}
        <TotalResults total={total} />
      </div>

      {/* Estado vacío - Empty state (solo cuando no está cargando ni fetching y no hay datos) */}
      {/* Empty state (only when not loading, not fetching, and no data) */}
      {!isFetching && data.length === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.PRODUCTS} />
      )}

      {/* Tabla de estadísticas - Statistics table (mostrar si hay datos o si está fetching para mantenerla visible durante búsquedas) */}
      {/* Statistics table (show if there's data or if fetching to keep it visible during searches) */}
      {(data.length > 0 || isFetching) && (
        <ProductStatisticsTable data={data} isFetching={isFetching} selectedColumns={selectedColumns} />
      )}
    </>
  );
};
